import React, { useEffect, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Select, { components } from 'react-select';
import { findInputError } from '../utils/findInputError';
import { isFormInvalid } from '../utils/isFormValid';
import InputError from './InputError';
import { capitalizeFirstLetter } from '../ReusableFunctions/reusableFunctions';
import { GLOBAL_STYLES } from '../appConstants';

const MultiSelectField = ({
    name,
    validation,
    label,
    className,
    list,
    isLoading,
    isSearchable = false,
    isDisabled,
    defaultValue,
    handleChange,
    placeholderText,
    styles,
    value,
    clearAll,
    ...restProps
}) => {
    const {
        formState: { errors },
        control,
        clearErrors,
    } = useFormContext();

    const previousValueRef = useRef(null); // Track previous value

    // Track controlled value explicitly
    const selectedValueRef = useRef(defaultValue);

    useEffect(() => {
        if (clearAll) {
            selectedValueRef.current = [];
        }
    }, [clearAll]);

    useEffect(() => {
        if (handleChange && selectedValueRef.current !== previousValueRef.current) {
            handleChange(selectedValueRef.current);
            previousValueRef.current = selectedValueRef.current; // Update previous value
        }
    }, [handleChange]);

    const inputError = findInputError(errors, name);
    const isInvalid = isFormInvalid(inputError);

    const defaultStyles = {
        control: (provided) => ({
            ...provided,
            fontSize: '12px',
            background: '#F3F3F3',
            borderRadius: '5px',
            borderColor: isInvalid ? 'red' : GLOBAL_STYLES.BORDER_COLOR,
            outline: 'none',
            fontWeight: 600,
            color: 'black',
            height: '3px',
            ...restProps.controlStyle,
        }),
        option: (provided, state) => ({
            ...provided,
            fontSize: '12px',
            color: state.isFocused && !state.isSelected ? 'black' : state.isSelected ? 'white' : 'black',
            backgroundColor: state.isFocused && !state.isSelected
                ? 'rgba(0, 0, 0, 0.1)'
                : state.isSelected
                ? GLOBAL_STYLES.BG_ACTIVE
                : GLOBAL_STYLES.BG_INACTIVE,
            minHeight: '3px',
            ...restProps.optionStyle,
        }),
    };

    const Option = (props) => (
        <components.Option {...props}>
            <div className="flex gap-4">
                <input type="checkbox" checked={props.isSelected} onChange={() => null} />{' '}
                <label>{props.label}</label>
            </div>
        </components.Option>
    );

    const MultiValue = ({ index, getValue, ...props }) => {
        const maxToShow = 4;
        const selected = getValue();

        if (index < maxToShow) {
            return <components.MultiValue {...props} />;
        }

        if (index === maxToShow) {
            return <div className="multi-value-plus">+{selected.length - maxToShow}</div>;
        }

        return null;
    };

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                rowGap: '5px',
                position: 'relative',
                ...restProps.containerStyle,
            }}
        >
            {label && (
                <div style={{ fontSize: '13px', color: '#3B3B3B' }}>
                    {capitalizeFirstLetter(label)}
                </div>
            )}
            <Controller
                name={name}
                control={control}
                defaultValue={defaultValue}
                rules={validation}
                render={({ field }) => (
                    <Select
                        {...field}
                        name={name}
                        menuPlacement="auto"
                        className={className || ''}
                        classNamePrefix="react-select"
                        options={list}
                        placeholder={placeholderText || 'Select'}
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        styles={defaultStyles}
                        isLoading={isLoading}
                        value={field.value || defaultValue}
                        components={{ Option, MultiValue }}
                        onChange={(selectedOptions) => {
                            selectedValueRef.current = selectedOptions; // Update the controlled value
                            field.onChange(selectedOptions); // Inform react-hook-form
                            clearErrors(); // Clear errors after successful change
                        }}
                        theme={(theme) => ({
                            ...theme,
                            colors: {
                                ...theme.colors,
                                primary25: GLOBAL_STYLES.BG_ACTIVE,
                                primary: GLOBAL_STYLES.BG_ACTIVE,
                            },
                        })}
                        isSearchable={isSearchable}
                        isDisabled={isDisabled}
                        isMulti
                        {...restProps}
                    />
                )}
            />
            {isInvalid && <InputError inputError={inputError} />}
        </div>
    );
};

export default MultiSelectField;
